/** Shared schedule calendar constants — colors keyed by Visit Type. */
import { getFormsBySection } from "../../clinicalFormCatalog";

/** Section headings reused for the visit-type <optgroup> labels. */
export const EVAL_SECTION_LABELS = {
  "soc-roc-recert": "SOC / ROC / Recert / Transfer",
  "nurse-notes": "Nurse Eval & Visit Notes",
  "pt-notes": "PT Eval & Visit Notes",
  "ot-notes": "OT Eval & Visit Notes",
  "st-notes": "ST Eval & Visit Notes",
  "msw-notes": "MSW Eval & Visit Notes",
  "chha-notes": "CHHA Visit Notes",
};

export const VISIT_TYPES = [
  { value: "SOC", label: "SOC", color: "#1d4ed8" },
  { value: "EVALUATION", label: "EVALUATION", color: "#7c3aed" },
  { value: "FOLLOW_UP", label: "FOLLOW UP", color: "#0369a1" },
  { value: "REGULAR_DC", label: "REGULAR DC", color: "#b45309" },
  { value: "OASIS_DC", label: "OASIS DC", color: "#c2410c" },
  { value: "ROC", label: "ROC", color: "#0f766e" },
  { value: "REASSESSMENT", label: "REASSESSMENT", color: "#4f46e5" },
  { value: "ROUTINE", label: "ROUTINE", color: "#334155" },
  { value: "PRN", label: "PRN", color: "#be185d" },
  { value: "SUPERVISORY", label: "SUPERVISORY", color: "#047857" },
  { value: "RECERT", label: "RECERT", color: "#9333ea" },
];

export const VISIT_TYPE_COLORS = Object.fromEntries(
  VISIT_TYPES.map((t) => [t.value, t.color]),
);

/** Calendar chip ring: visit scheduled outside View Frequency. */
export const FREQUENCY_MISSING_HEX = "#eab308";
export const FREQUENCY_MISSING_RING =
  "ring-2 ring-yellow-400 ring-offset-1";
export const FREQUENCY_MISSING_LABEL = "Frequency missing";
export const FREQUENCY_MISSING_SWATCH_SHADOW = `0 0 0 2px #fff, 0 0 0 4px ${FREQUENCY_MISSING_HEX}`;

/**
 * Visit-type inset stays. Yellow and red rings stack so both states are visible:
 * yellow inside, red outside, with a white gap between them.
 */
export function visitChipBoxShadow({
  typeColor,
  filled,
  frequencyMissing,
  unauthorized,
  unauthorizedHex,
}) {
  const layers = [];
  if (!filled && typeColor) {
    layers.push(`inset 0 0 0 1px ${typeColor}`);
  }
  if (frequencyMissing && unauthorized && unauthorizedHex) {
    layers.push(
      `0 0 0 2px ${FREQUENCY_MISSING_HEX}`,
      `0 0 0 4px #fff`,
      `0 0 0 6px ${unauthorizedHex}`,
    );
  } else if (unauthorized && unauthorizedHex) {
    layers.push(`0 0 0 2px #fff`, `0 0 0 4px ${unauthorizedHex}`);
  } else if (frequencyMissing) {
    layers.push(`0 0 0 2px #fff`, `0 0 0 4px ${FREQUENCY_MISSING_HEX}`);
  }
  return layers.length ? layers.join(", ") : undefined;
}

export const DISCIPLINES = [
  { value: "RN", label: "RN (Registered Nurse)" },
  { value: "LVN", label: "LVN (Licensed Vocational Nurse)" },
  { value: "PT", label: "PT (Physical Therapy)" },
  { value: "PTA", label: "PTA (Physical Therapist Assistant)" },
  { value: "OT", label: "OT (Occupational Therapy)" },
  { value: "OTA", label: "OTA (Occupational Therapy Assistant)" },
  { value: "ST", label: "ST (Speech Therapy)" },
  { value: "MSW", label: "MSW (Medical Social Worker)" },
  { value: "HHA", label: "HHA (Home Health Aide)" },
  { value: "CG", label: "CG (Caregiver)" },
  { value: "CNA", label: "CNA (Certified Nursing Assistant)" },
  { value: "CHHA", label: "CHHA (HHA / CG / CNA)" },
];

/**
 * View Frequency discipline picker. HHA is hidden; CHHA stays as a position.
 */
export const FREQUENCY_DISCIPLINES = DISCIPLINES.filter(
  (d) => d.value !== "HHA",
);

/**
 * Schedule discipline → assignment role codes.
 * Each visitable role is 1:1 with assigned staff. CHHA is kept for older visits.
 */
export const DISCIPLINE_TO_ROLES = {
  RN: ["RN"],
  LVN: ["LVN"],
  PT: ["PT"],
  PTA: ["PTA"],
  OT: ["OT"],
  OTA: ["OTA"],
  ST: ["ST"],
  MSW: ["MSW"],
  HHA: ["HHA"],
  CG: ["CG"],
  CNA: ["CNA"],
  CHHA: ["HHA", "CNA", "CG"],
};

export const getRolesForDiscipline = (discipline) => {
  const key = String(discipline || "").toUpperCase();
  return DISCIPLINE_TO_ROLES[key] || [];
};

/** Filter assigned staff list to roles that match the selected discipline. */
export const filterStaffByDiscipline = (assignedStaff, discipline) => {
  const roles = new Set(getRolesForDiscipline(discipline));
  if (!roles.size) return [];
  return (assignedStaff || []).filter((s) => {
    const role = String(s.role || "").toUpperCase();
    const status = String(s.status || "Active");
    if (status && status !== "Active") return false;
    return roles.has(role);
  });
};

export const getStaffEmployeeId = (staff) =>
  staff?.employeeId ?? staff?.userId ?? staff?.id ?? null;
export const getVisitTypeColor = (visitType, explicitColor) => {
  if (explicitColor && /^#[0-9A-Fa-f]{6}$/.test(explicitColor)) {
    return explicitColor;
  }
  const key = String(visitType || "")
    .toUpperCase()
    .replace(/\s+/g, "_");
  return VISIT_TYPE_COLORS[key] || "#64748b";
};

export const formatVisitTypeLabel = (visitType) => {
  const found = VISIT_TYPES.find((t) => t.value === visitType);
  if (found) return found.label;
  return String(visitType || "").replace(/_/g, " ");
};

/** Map schedule discipline → Eval & Orders section key. */
const DISCIPLINE_TO_EVAL_SECTION = {
  RN: "nurse-notes",
  LVN: "nurse-notes",
  PT: "pt-notes",
  PTA: "pt-notes",
  OT: "ot-notes",
  OTA: "ot-notes",
  ST: "st-notes",
  MSW: "msw-notes",
  HHA: "chha-notes",
  CG: "chha-notes",
  CNA: "chha-notes",
  CHHA: "chha-notes",
};

const SOC_ROC_RECERT_TYPES = new Set(["SOC", "ROC", "RECERT", "OASIS_DC"]);
/** LVN, MSW and CHHA never perform OASIS assessments. */
const SOC_ROC_RECERT_DISCIPLINES = new Set(["RN", "PT", "OT", "ST"]);

/**
 * Resolve which Eval & Orders section a scheduled visit belongs to.
 */
export const resolveEvalSectionFromVisit = (discipline, visitType) => {
  const disc = String(discipline || "").toUpperCase();
  const type = String(visitType || "")
    .toUpperCase()
    .replace(/\s+/g, "_");

  if (SOC_ROC_RECERT_TYPES.has(type) && SOC_ROC_RECERT_DISCIPLINES.has(disc)) {
    return "soc-roc-recert";
  }

  return DISCIPLINE_TO_EVAL_SECTION[disc] || "physician-orders";
};

export const getEvalSectionForDiscipline = (discipline) =>
  DISCIPLINE_TO_EVAL_SECTION[String(discipline || "").toUpperCase()] || null;

/** Schedule discipline → the `<prefix>_*` naming used by that discipline's forms. */
const DISCIPLINE_FORM_PREFIX = {
  RN: "rn",
  LVN: "rn",
  PT: "pt",
  PTA: "pt",
  OT: "ot",
  OTA: "ot",
  ST: "st",
  MSW: "msw",
  HHA: "chha",
  CG: "chha",
  CNA: "chha",
  CHHA: "chha",
};

/**
 * The forms a visit of this discipline may be booked against, grouped for an
 * <optgroup> list. Only the selected position's own forms are offered: its
 * notes plus its SOC/ROC/Recert/Transfer assessments. MSW and CHHA never
 * perform SOC/ROC/Recert, so they only see their own notes.
 *
 * Mirrors schedulableFormsForDiscipline() in the backend patient constants.
 */
export const getSchedulableFormGroups = (discipline) => {
  const disc = String(discipline || "").toUpperCase();
  if (disc === "CG" || disc === "CNA") return [];
  const section = getEvalSectionForDiscipline(disc);
  if (!section) return [];
  const prefix = DISCIPLINE_FORM_PREFIX[disc];
  const groups = [
    { section, label: EVAL_SECTION_LABELS[section] || section, forms: getFormsBySection(section) },
  ];
  if (SOC_ROC_RECERT_DISCIPLINES.has(disc)) {
    groups.push({
      section: "soc-roc-recert",
      label: EVAL_SECTION_LABELS["soc-roc-recert"],
      forms: getFormsBySection("soc-roc-recert").filter((f) =>
        f.formType.startsWith(`${prefix}_`),
      ),
    });
  }
  return groups.filter((g) => g.forms.length);
};

/** CG / CNA: Daily Log only. Other roles: that position's visit notes. */
export const getAssignableFormGroups = (discipline, staffRole) => {
  const role = String(staffRole || "").toUpperCase();
  const disc = String(discipline || "").toUpperCase();
  if (role === "CG" || role === "CNA" || disc === "CG" || disc === "CNA") {
    return [
      {
        section: "daily-logs",
        label: "Daily Logs",
        forms: [{ formType: ASSIGNED_DAILY_LOG_FORM_TYPE, formName: "Daily Log" }],
      },
    ];
  }
  return getSchedulableFormGroups(discipline);
};

/**
 * Visit type is derived from the chosen form rather than asked for, so the
 * calendar colour always agrees with the note being written. Longest suffix
 * wins; anything unrecognised is a routine visit.
 *
 * Mirrors resolveVisitTypeFromFormType() in the backend patient constants.
 */
const FORM_TYPE_VISIT_TYPE_SUFFIXES = [
  ["_oasis_discharge_note", "OASIS_DC"],
  ["_discharge_non_visit", "REGULAR_DC"],
  ["_discharge_note", "REGULAR_DC"],
  ["_transfer_note", "ROUTINE"],
  ["_recert_note", "RECERT"],
  ["_reeval_note", "REASSESSMENT"],
  ["_missed_visit", "ROUTINE"],
  ["_visit_note", "ROUTINE"],
  ["_sup_visit", "SUPERVISORY"],
  ["_eval_note", "EVALUATION"],
  ["_soc_note", "SOC"],
  ["_roc_note", "ROC"],
].sort((a, b) => b[0].length - a[0].length);

export const resolveVisitTypeFromFormType = (formType) => {
  const key = String(formType || "").toLowerCase();
  if (key === ASSIGNED_DAILY_LOG_FORM_TYPE) return "ROUTINE";
  const match = FORM_TYPE_VISIT_TYPE_SUFFIXES.find(([suffix]) =>
    key.endsWith(suffix),
  );
  return match ? match[1] : "ROUTINE";
};

export const ASSIGNED_DAILY_LOG_FORM_TYPE = "daily_log";

const DEFAULT_VISIT_NOTE_BY_DISCIPLINE = {
  RN: "rn_visit_note",
  LVN: "rn_visit_note",
  PT: "pt_visit_note",
  PTA: "pt_visit_note",
  OT: "ot_visit_note",
  OTA: "ot_visit_note",
  ST: "st_visit_note",
  MSW: "msw_visit_note",
  HHA: "chha_visit_note",
  CG: "chha_visit_note",
  CNA: "chha_visit_note",
  CHHA: "chha_visit_note",
};

export const defaultAssignedFormForStaff = (discipline, staffRole) => {
  const role = String(staffRole || "").toUpperCase();
  const disc = String(discipline || "").toUpperCase();
  if (role === "CG" || role === "CNA" || disc === "CG" || disc === "CNA") {
    return { formType: ASSIGNED_DAILY_LOG_FORM_TYPE, formKind: "daily_log" };
  }
  const formType =
    DEFAULT_VISIT_NOTE_BY_DISCIPLINE[disc] || "rn_visit_note";
  return { formType, formKind: "clinical" };
};
